import { AioIconButton, InTextLink } from "aic";
import { useAppSettings } from "contexts/AppSettingsContext";
import { NewsItem } from "functions/api/news/loadNews";
import { useEffect, useState, useTransition } from "react";
import { Form } from "react-bootstrap";
import { Pen } from "react-bootstrap-icons";

export interface newsItemsDisplayProps {
  id: string;
  item: NewsItem;
  setItem?: (ret: NewsItem) => void;
  removeItem?: () => void;
}

/**
 * Display a single news item with optional edit and remove controls. When
 * `setItem` is supplied the item becomes editable inline and changes are
 * returned via the callback.
 *
 * @param props - Destructured props object.
 * @param props.id - Element id prefix used for internal controls.
 * @param props.item - The `NewsItem` data to display.
 * @param props.setItem - Optional callback invoked with updated item data.
 * @param props.removeItem - Optional callback invoked to remove the item.
 */
export const NewsItemsDisplay = ({
  id,
  item,
  setItem,
  removeItem,
}: newsItemsDisplayProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const appSettings = useAppSettings();
  const [editing, setEditing] = useState<boolean>(false);

  // Hold current values and send back onBlur
  const [currentTitle, setCurrentTitle] = useState(item.title);
  const [currentText, setCurrentText] = useState(item.text);
  useEffect(() => {
    startTransition(() => {
      setCurrentTitle(item.title);
      setCurrentText(item.text);
    });
  }, [item]);

  /**
   * Return updated data
   */
  const returnData = () => {
    const nN: NewsItem = {
      title: currentTitle,
      text: currentText,
      displayName:
        appSettings.displayName ??
        // istanbul ignore next
        "Test user",
      timestamp: Date.now(),
    };
    setItem?.(nN);
  };

  return (
    <div
      id={id}
      className="pb-2"
    >
      <div style={{ display: "flex" }}>
        <div style={{ minWidth: "100px" }}>
          {new Date(item.timestamp).toISOString().split("T")[0]}
        </div>
        <div
          style={{ minWidth: "32px", width: "32px" }}
          className="align-items-center justify-items-center"
        >
          {typeof removeItem === "function" ? (
            <>
              <AioIconButton
                id={`${id}-remove`}
                onClick={() => {
                  removeItem();
                }}
                iconName="aioxMinus"
                tipText="Remove news item"
                aria-label="Remove news item"
                style={{ paddingLeft: "8px", paddingRight: "8px" }}
              />
              <Pen
                id={`${id}-edit`}
                onClick={() => setEditing(!editing)}
                style={{ marginLeft: "8px" }}
                aria-label="Edit news item"
              />
            </>
          ) : (
            <>&nbsp;</>
          )}
        </div>
        <div style={{ flexGrow: 1 }}>
          {setItem && editing ? (
            <Form.Control
              id={`${id}-title-editor`}
              style={{ marginBottom: "4px" }}
              value={currentTitle}
              placeholder="Add title"
              onChange={(e) => setCurrentTitle(e.currentTarget.value)}
              onBlur={() => returnData()}
              aria-label="News title"
              spellCheck={true}
            />
          ) : item.title !== "" ? (
            <h5
              id={`${id}-title`}
              className="mb-0"
            >
              {item.title}
            </h5>
          ) : (
            <></>
          )}
          <small>
            <em>
              {
                // istanbul ignore next
                item.displayName ??
                  appSettings.displayName ??
                  // istanbul ignore next
                  "Test user"
              }
            </em>
          </small>
          {setItem && editing ? (
            <Form.Control
              id={`${id}-news-editor`}
              as="textarea"
              rows={3}
              value={currentText}
              onChange={(e) => setCurrentText(e.currentTarget.value)}
              placeholder="Add news"
              onBlur={() => returnData()}
              aria-label="News text"
              spellCheck={true}
            />
          ) : (
            <div id={`${id}-news`}>
              <InTextLink text={item.text} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

NewsItemsDisplay.displayName = "NewsItemsDisplay";
